import { useState, useRef } from 'react'
import { auth } from '@/api/client'
import { useGame } from '@/context/GameContext'

export default function StudentLogin() {
  const { loginStudent, loginTeacher } = useGame()
  const [mode, setMode] = useState<'student' | 'teacher'>('student')

  // Student form. A join link (https://<host>/?class=CODE) prefills the class
  // code so the only thing a student types is their name.
  const [classCode, setClassCode] = useState(() => readClassFromUrl())
  const [name, setName]           = useState('')
  const nameRef = useRef<HTMLInputElement>(null)

  // Teacher form
  const [email, setEmail]         = useState('')
  const [password, setPassword]   = useState('')

  const [loading, setLoading]     = useState(false)
  const [error, setError]         = useState('')

  async function handleStudentSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    if (!classCode.trim() || !name.trim()) {
      setError('Enter your class code and name')
      return
    }
    // Instant feedback; the server does the authoritative profanity check.
    const trimmed = name.trim()
    if (trimmed.length < 2) {
      setError('Please enter your name (at least 2 letters)')
      return
    }
    if (!/^[\p{L}\p{M}][\p{L}\p{M} '.\-]*$/u.test(trimmed)) {
      setError('Names can only use letters, spaces, hyphens, and apostrophes')
      return
    }
    setLoading(true)
    try {
      const { token, student, class: cls } = await auth.studentLogin(classCode.trim().toUpperCase(), name.trim())
      loginStudent(student, cls, token)
    } catch (err: any) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  async function handleTeacherSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      const { token, teacher } = await auth.teacherLogin(email.trim().toLowerCase(), password)
      loginTeacher(teacher, token)
    } catch (err: any) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4"
         style={{ background: 'radial-gradient(ellipse at center, #1e1b4b 0%, #0a0a1a 70%)' }}>

      {/* Pixel star background */}
      <div className="fixed inset-0 pointer-events-none" style={{ zIndex: 0 }}>
        {Array.from({ length: 60 }, (_, i) => (
          <div key={i} className="absolute rounded-full"
               style={{
                 width: i % 4 === 0 ? 3 : 1,
                 height: i % 4 === 0 ? 3 : 1,
                 left: `${(i * 137) % 100}%`,
                 top: `${(i * 97) % 100}%`,
                 background: 'white',
                 opacity: 0.3 + (i % 5) * 0.1,
               }} />
        ))}
      </div>

      <div className="relative z-10 flex flex-col items-center w-full">
        {/* Title */}
        <div className="mb-8 text-center">
          <h1 className="mb-1" style={{
            fontFamily: '"Press Start 2P", monospace',
            fontSize: 'clamp(14px, 3.2vw, 26px)',
            lineHeight: 1.5,
            color: '#a78bfa',
            textShadow: '0 0 20px #6366f1, 0 0 40px #4338ca, 2px 2px 0 #1e1b4b',
          }}>
            ⚔️ SUPER CIVICA WORLD
          </h1>
          <p style={{ fontFamily: '"Press Start 2P", monospace', fontSize: '8px', color: '#6366f1' }}>
            Michigan Social Studies · Grades 6–8
          </p>
        </div>

        {/* Mode tabs */}
        <div className="flex gap-2 mb-6">
          {(['student', 'teacher'] as const).map(m => (
            <button key={m}
              onClick={() => { setMode(m); setError('') }}
              className="px-5 py-2 rounded transition-all"
              style={{
                fontFamily: '"Press Start 2P", monospace', fontSize: '8px',
                background: mode === m ? '#4f46e5' : '#1e1b4b',
                color: mode === m ? '#ffffff' : '#818cf8',
                border: `2px solid ${mode === m ? '#818cf8' : '#312e81'}`,
                cursor: 'pointer',
              }}>
              {m === 'student' ? '🎮 STUDENT' : '📊 TEACHER'}
            </button>
          ))}
        </div>

        {/* Login panel */}
        <div className="w-full max-w-sm rounded-xl p-6" style={{
          background: '#0f0f23',
          border: '2px solid #312e81',
          boxShadow: '0 0 40px rgba(99,102,241,0.2), inset 0 1px 0 rgba(255,255,255,0.05)',
        }}>

          {mode === 'student' ? (
            <form onSubmit={handleStudentSubmit} className="space-y-4">
              <div>
                <label className="block mb-1" style={{ fontFamily: '"Press Start 2P", monospace', color: '#818cf8', fontSize: '7px' }}>
                  CLASS CODE
                </label>
                <input
                  value={classCode}
                  onChange={e => setClassCode(e.target.value.toUpperCase())}
                  placeholder="Enter class code"
                  className="w-full px-3 py-2 rounded outline-none"
                  style={{ fontFamily: '"Press Start 2P", monospace', fontSize: '12px',
                           background: '#1e1b4b', border: '2px solid #4338ca', color: '#e0e7ff',
                           letterSpacing: '0.15em' }}
                />
              </div>
              <div>
                <label className="block mb-1" style={{ fontFamily: '"Press Start 2P", monospace', color: '#818cf8', fontSize: '7px' }}>
                  YOUR NAME
                </label>
                <input
                  ref={nameRef}
                  autoFocus={classCode.length > 0}
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="Enter your name"
                  className="w-full px-3 py-2 rounded outline-none"
                  style={{ fontFamily: '"Press Start 2P", monospace', fontSize: '10px',
                           background: '#1e1b4b', border: '2px solid #4338ca', color: '#e0e7ff' }}
                />
              </div>
              {error && <p style={{ fontFamily: '"Press Start 2P", monospace', fontSize: '7px', color: '#ef4444' }}>❌ {error}</p>}
              <button type="submit" disabled={loading}
                className="w-full py-3 rounded transition-all hover:scale-105 active:scale-95"
                style={{ fontFamily: '"Press Start 2P", monospace', fontSize: '10px',
                         background: loading ? '#312e81' : '#4f46e5', color: '#ffffff',
                         border: '2px solid #818cf8', cursor: 'pointer' }}>
                {loading ? 'ENTERING...' : '▶  ENTER WORLD'}
              </button>
            </form>
          ) : (
            <form onSubmit={handleTeacherSubmit} className="space-y-4">
              <div>
                <label className="block mb-1" style={{ fontFamily: '"Press Start 2P", monospace', color: '#818cf8', fontSize: '7px' }}>
                  EMAIL
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="teacher@school.com"
                  className="w-full px-3 py-2 rounded outline-none"
                  style={{ fontFamily: 'monospace', fontSize: '13px',
                           background: '#1e1b4b', border: '2px solid #4338ca', color: '#e0e7ff' }}
                />
              </div>
              <div>
                <label className="block mb-1" style={{ fontFamily: '"Press Start 2P", monospace', color: '#818cf8', fontSize: '7px' }}>
                  PASSWORD
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="w-full px-3 py-2 rounded outline-none"
                  style={{ fontFamily: 'monospace', fontSize: '13px',
                           background: '#1e1b4b', border: '2px solid #4338ca', color: '#e0e7ff' }}
                />
              </div>
              {error && <p style={{ fontFamily: '"Press Start 2P", monospace', fontSize: '7px', color: '#ef4444' }}>❌ {error}</p>}
              <button type="submit" disabled={loading}
                className="w-full py-3 rounded"
                style={{ fontFamily: '"Press Start 2P", monospace', fontSize: '10px',
                         background: '#4f46e5', color: '#ffffff', border: '2px solid #818cf8', cursor: 'pointer' }}>
                {loading ? 'LOGGING IN...' : '📊 TEACHER LOGIN'}
              </button>
            </form>
          )}
        </div>

        <p className="mt-8" style={{ fontFamily: '"Press Start 2P", monospace', fontSize: '6px', color: '#374151' }}>
          Michigan GLCE · M-STEP Aligned · Grades 6–8
        </p>
      </div>
    </div>
  )
}

/** Class code from a join link's ?class= param, normalised like the input is. */
function readClassFromUrl(): string {
  if (typeof window === 'undefined') return ''
  const raw = new URLSearchParams(window.location.search).get('class') || ''
  return raw.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 12)
}
